import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { reduce, camelCase, lowerCase } from 'lodash';
import * as Yup from 'yup';

import ReferenceDataMegaCollection from '~/components/ReferenceData/MegaCollection';

import {
  referenceAdminInstance as refAdmin,
} from '~/utils';

const ReferenceDataContext = createContext();

const UUIDRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

const parseFieldType = (columnInfo, title) => {
  if (
    title !== 'market' &&
    title !== 'specialty' &&
    title !== 'subSpecialty'
  ) {
    if (lowerCase(title).includes('color')) {
      return {
        type: 'color',
        validationSchema: Yup.string().matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
      };
    } else if (lowerCase(title).includes('terms of use body')) {
      return {
        type: 'textarea',
        validationSchema: Yup.string(),
      };
    }

    if (columnInfo.type === 'BOOLEAN') {
      return {
        type: 'checkbox',
        validationSchema: Yup.boolean(),
      };
    } else if (columnInfo.type === 'BIGINT' || columnInfo.type === 'INTEGER') {
      return {
        type: 'number',
        validationSchema: Yup.number().integer(),
      };
    } else if (columnInfo.type === 'TYPEATTR') {
      return {
        type: 'select',
        validationSchema: Yup.object().nullable(),
      };
    } else if (columnInfo.type === 'TIMESTAMP') {
      return {
        type: 'date',
        validationSchema: Yup.date(),
      };
    } else if (columnInfo.type === 'UUID') {
      if (columnInfo.references) {
        return {
          type: 'select',
          validationSchema: Yup.object().shape({
            id: Yup.string().matches(UUIDRegex),
          }),
        };
      }

      return {
        type: 'text',
        validationSchema: Yup.string().matches(UUIDRegex),
      };
    } else if (columnInfo.type.includes('VARCHAR')) {
      return {
        type: 'text',
        validationSchema: Yup.string().max(parseInt(columnInfo.type.replace(/\D/g, ''), 10)),
      };
    }

    return {
      type: 'text',
      validationSchema: Yup.string(),
    };
  }

  return {
    type: 'text',
    validationSchema: Yup.string(),
  };
};

const ReferenceDataProvider = ({
  type,
  config,
}) => {
  const [meta, setMeta] = useState({});

  const validationSchema = (fields) => {
    const f = reduce(config.customFields, (a, v, k) => ({
      ...a,
      [k]: v.validationSchema
        ? v.validationSchema
        : Yup.mixed().notRequired(),
    }), {
      isActive: Yup.boolean(),
      ...(reduce(fields, (a, v, k) => ({
        ...a,
        [k]: v.required && !v.primaryKey && v.type !== 'BOOLEAN'
          ? parseFieldType(v, k).validationSchema.required()
          : parseFieldType(v, k).validationSchema.nullable().notRequired(),
      }), {})),
    });

    return Yup.object().shape(f);
  };

  const fetchReferenceData = t =>
    refAdmin
      .get(t)
      .then(r => r.data)
      .then((r) => {
        const { meta: m } = r;

        m.nameAttr = camelCase(m.nameAttr);
        m.labelAttr = camelCase(m.labelAttr);

        if (m.typeAttr) {
          m.typeAttr = camelCase(m.typeAttr);
        }

        const f = reduce(m.fields, (a, v, k) => {
          const field = v;

          if (!v.primaryKey && v.references) {
            refAdmin
              .get(v.references, {
                'page-size': 10000,
                page: 1,
              })
              .then(rr => rr.data)
              .then((rr) => {
                field.options = rr.data;
                field.getOptionValue = o => o.id;
                field.getOptionLabel = o => `${o.attributes[camelCase(rr.meta.labelAttr)]} (${o.id})`;
              });
          }

          return {
            ...a,
            [k]: field,
          };
        }, {});

        if (m.availableTypes && m.typeAttr) {
          f[m.typeAttr].options = m.availableTypes.map(value => ({ value, label: value }));
          f[m.typeAttr].type = 'TYPEATTR';
          f[m.typeAttr].creatable = true;
        }

        setMeta({
          ...m,
          ...f,
          validationSchema: validationSchema(f),
        });
      });

  useEffect(() => {
    if (type) {
      fetchReferenceData(type);
    }
  }, [type]);

  return (
    <ReferenceDataContext.Provider
      value={{
        type,
        meta,
        config: {
          ...config,
          actions: {
            ...config.actions,
            parseFieldType,
            fetchReferenceData,
          },
        },
      }}
    >
      <ReferenceDataMegaCollection />
    </ReferenceDataContext.Provider>
  );
};

ReferenceDataProvider.propTypes = {
  type: PropTypes.string.isRequired,
  config: PropTypes.shape({
    components: PropTypes.shape({
      form: PropTypes.func.isRequired,
      beforeCollection: PropTypes.func.isRequired,
    }),
    customFields: PropTypes.shape({
      serializeValue: PropTypes.func,
      normalizeValue: PropTypes.func,
      validationSchema: PropTypes.shape(),
    }),
    actions: PropTypes.shape({
      afterRootSubmitSuccess: PropTypes.func,
    }),
  }),
};

ReferenceDataProvider.defaultProps = {
  config: {
    customFields: {},
    actions: {
      afterRootSubmitSuccess: r => r,
    },
  },
};

export default ReferenceDataProvider;
export { ReferenceDataContext };
